import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faChevronLeft,
  faObjectsAlignLeft,
  faObjectsAlignRight,
  faTimesCircle,
  faTrashAlt,
  faUnlink,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Editor } from '@tiptap/core'
import { BubbleMenu } from '@tiptap/react'
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { EditorCommandButton } from './EditorCommandButton'
import { EditorCommandSpacer } from './EditorCommandSpacer'

interface IProps {
  editor: Editor | null
  disabled?: boolean
}

export const EditorImageBubbleMenu = ({ editor, disabled }: IProps) => {
  const [view, setView] = useState<'default' | 'alt-text' | 'link'>('default')
  const ref = useRef<HTMLDivElement>(null)
  const unblur = useCallback(() => {
    if (editor?.isActive('spacer')) {
      editor?.chain().blur().run()
    }
  }, [editor])

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        unblur()
      }
    }

    const handleClickOutside = (e: any) => {
      if (!ref.current?.contains(e.target)) {
        unblur()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', down)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', down)
    }
  }, [unblur])

  const hasImageExtension: boolean = !!editor?.extensionManager.extensions.find(
    (e) => e.name === 'image',
  )
  if (!editor || disabled || !hasImageExtension) return null
  const isEditingAltText = view === 'alt-text'
  const isEditingLink = view === 'link'
  const isDefaultView = view === 'default' || (!isEditingAltText && !isEditingLink)
  return (
    <BubbleMenu
      className="bg-body-tertiary border rounded-xs p-2"
      tippyOptions={{
        duration: 150,
        zIndex: 1060,
        placement: 'bottom-end',
        moveTransition: 'transform 0.15s ease-out',
      }}
      editor={editor}
      shouldShow={({ editor: e }) => e.isActive('image')}
    >
      <div ref={ref}>
        {isDefaultView && (
          <HomeImageMenu
            editor={editor}
            onClickAltText={() => setView('alt-text')}
            onClickLink={() => setView('link')}
          />
        )}
        {isEditingAltText && <AltTextMenu editor={editor} onBack={() => setView('default')} />}
        {isEditingLink && <LinkMenu editor={editor} onBack={() => setView('default')} />}
      </div>
    </BubbleMenu>
  )
}

interface IHomeImageMenuProps {
  editor: Editor
  onClickAltText: () => void
  onClickLink: () => void
}

const HomeImageMenu = ({
  editor,
  onClickAltText: handleClickAltText,
  onClickLink: handleClickLink,
}: IHomeImageMenuProps) => (
  <>
    <EditorCommandButton
      title="Align left"
      className="btn btn-sm"
      isActive={() => editor.getAttributes('image').alignment === 'left'}
      onClick={() => {
        if (editor.getAttributes('image').alignment === 'left') {
          editor.chain().setImageAttributes({ alignment: 'left' }).run()
        } else {
          editor.chain().setImageAttributes({ alignment: 'left' }).run()
        }
      }}
    >
      <FontAwesomeIcon icon={faAlignLeft} fixedWidth />
    </EditorCommandButton>
    <EditorCommandButton
      title="Align center"
      className="btn btn-sm"
      isActive={() => editor.getAttributes('image').alignment === 'center'}
      onClick={() => {
        if (editor.getAttributes('image').alignment === 'center') {
          editor.chain().setImageAttributes({ alignment: 'left' }).run()
        } else {
          editor.chain().setImageAttributes({ alignment: 'center' }).run()
        }
      }}
    >
      <FontAwesomeIcon icon={faAlignCenter} fixedWidth />
    </EditorCommandButton>
    <EditorCommandButton
      title="Align right"
      className="btn btn-sm"
      isActive={() => editor.getAttributes('image').alignment === 'right'}
      onClick={() => {
        if (editor.getAttributes('image').alignment === 'right') {
          editor.chain().setImageAttributes({ alignment: 'left' }).run()
        } else {
          editor.chain().setImageAttributes({ alignment: 'right' }).run()
        }
      }}
    >
      <FontAwesomeIcon icon={faAlignRight} fixedWidth />
    </EditorCommandButton>
    <EditorCommandSpacer className="px-1 pb-0" />
    <EditorCommandButton
      title="Wrap left"
      className="btn btn-sm"
      isActive={() => editor.getAttributes('image').float === 'left'}
      onClick={() => editor.chain().setImageAttributes({ float: 'left' }).run()}
    >
      <FontAwesomeIcon icon={faObjectsAlignLeft} fixedWidth />
    </EditorCommandButton>
    <EditorCommandButton
      title="Wrap right"
      className="btn btn-sm"
      isActive={() => editor.getAttributes('image').float === 'right'}
      onClick={() => editor.chain().setImageAttributes({ float: 'right' }).run()}
    >
      <FontAwesomeIcon icon={faObjectsAlignRight} fixedWidth />
    </EditorCommandButton>
    {/* TODO: Introduce link image */}
    {/* <EditorCommandSpacer className="px-1 pb-0" />
    <EditorCommandButton title="Add Link" className="btn btn-sm" onClick={() => handleClickLink()}>
      <FontAwesomeIcon icon={faLink} fixedWidth={true} />
    </EditorCommandButton> */}
    <EditorCommandSpacer className="px-1 pb-0" />
    <EditorCommandButton
      name="alt-text"
      className="btn btn-sm text-capitalize"
      onClick={() => handleClickAltText()}
    >
      Alt text
    </EditorCommandButton>
    <EditorCommandSpacer className="px-1 pb-0" />
    <EditorCommandButton
      title="Delete"
      className="btn btn-sm"
      onClick={() => editor.chain().deleteSelection().run()}
    >
      <FontAwesomeIcon icon={faTrashAlt} fixedWidth={true} />
    </EditorCommandButton>
  </>
)

const AltTextMenu = ({ editor, onBack: handleBack }: { editor: Editor; onBack: () => void }) => (
  <>
    <div className="input-group">
      <button
        className="btn btn-sm btn-link text-body"
        style={{ minWidth: '2rem' }}
        type="button"
        onClick={() => handleBack()}
      >
        <FontAwesomeIcon icon={faChevronLeft} fixedWidth />
      </button>
      <input
        type="text"
        className="form-control form-control-sm border-0"
        placeholder="Describe this image with alt text"
        value={editor.getAttributes('image').alt || ''}
        onChange={(e) => {
          editor
            .chain()
            .setImageAttributes({
              alt: e.target.value,
            })
            .run()
          e.target.focus()
        }}
      />
      {editor.getAttributes('image').alt && (
        <button
          className="btn btn-sm btn-link text-body"
          type="button"
          style={{ minWidth: '2rem' }}
          onClick={() => editor.chain().setImageAttributes({ alt: '' }).run()}
        >
          <FontAwesomeIcon icon={faTimesCircle} fixedWidth />
        </button>
      )}
    </div>
    <hr />
    <div
      className="small"
      style={{
        paddingLeft: '2.5rem',
        paddingRight: editor.getAttributes('image').alt ? '2.5rem' : 0,
      }}
    >
      Alt text is useful for people using screen readers because of visual limitations.
    </div>
  </>
)

const LinkMenu = ({
  editor,
  searchOptions,
  onBack: handleBack,
}: {
  editor: Editor
  searchOptions?: {
    search: string
    results: { url: string; label: React.JSX.Element | string }[]
    onSearch: (search: string) => Promise<any>
  }
  onBack: () => void
}) => (
  <>
    <div className="input-group">
      <button
        className="btn btn-sm btn-link text-body"
        style={{ minWidth: '2rem' }}
        type="button"
        onClick={() => handleBack()}
      >
        <FontAwesomeIcon icon={faChevronLeft} fixedWidth />
      </button>
      <input
        type="text"
        className="form-control form-control-sm border-0"
        placeholder={searchOptions?.onSearch ? 'Paste or search for link' : 'Paste link here'}
        value={
          editor?.state?.tr?.selection?.$anchor?.marks()?.[0]?.attrs?.href ||
          searchOptions?.search ||
          undefined
        }
        onChange={(e) => {
          searchOptions?.onSearch(e.target.value)
          const href = e.target.value.startsWith('http')
            ? e.target.value
            : 'http://' + e.target.value
          editor.chain().extendMarkRange('link').setLink({ href, target: '_blank' }).run()
        }}
      />
      <button
        className="btn btn-sm btn-link text-body"
        style={{ minWidth: '2rem' }}
        type="button"
        onClick={() => editor.chain().extendMarkRange('link').unsetLink().run()}
      >
        <FontAwesomeIcon icon={faUnlink} fixedWidth />
      </button>
    </div>
    {searchOptions?.onSearch && <hr />}
    {searchOptions?.results &&
      searchOptions.results.map(({ url, label }, i) => (
        <Fragment
          key={url
            .replace(/[^a-z0-9]+/gi, '-')
            .replace(/^-|-$/g, '')
            .toLowerCase()}
        >
          {i !== 0 && <hr />}
          <button
            className="dropdown-item"
            onClick={() => {
              const href = url.startsWith('http') ? url : 'http://' + url
              editor.chain().extendMarkRange('link').setLink({ href, target: '_blank' }).run()
            }}
          >
            {label}
          </button>
        </Fragment>
      ))}
  </>
)
